<script setup lang="ts">
import { computed, nextTick, ref, watch, type ComponentPublicInstance } from 'vue'
import Button from 'primevue/button'
import { useToast } from 'primevue/usetoast'
import {
  BookOpenText,
  Check,
  Copy,
  FileText,
  Lightbulb,
  Mic,
  RefreshCw,
  Search,
  Send,
  Sparkles,
} from 'lucide-vue-next'
import IconExpand from '@/assets/svg/expandIcon.svg?component'
import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import TypingIndicator from '@/components/atoms/typing/TypingIndicator.vue'
import type { AiCard, AiPanelState } from '@/components/organism/ia/types'
import { useI18n } from 'vue-i18n'
import AppInput from '@/components/atoms/inputs/AppInput.vue'

type FormattedSegment = {
  text: string
  bold?: boolean
  italic?: boolean
}

const props = defineProps<{
  cards: AiCard[]
  state: AiPanelState
  stateMessage?: string
  storageKey?: string
  showRefresh?: boolean
  onRefresh?: () => void | Promise<void>
  insightsTab?: 'summary' | 'suggestions'
  disableTranscript?: boolean
}>()

const emit = defineEmits<{
  'update:insightsTab': [tab: 'summary' | 'suggestions']
}>()

const { t } = useI18n()
const toast = useToast()

const isRefreshingIaStatus = ref(false)
const showPreviewModal = ref(false)
const selectedCardId = ref<string | null>(null)
const cardQuestionInputs = ref<Record<string, string>>({})
const copiedContent = ref(false)
const transcriptSearch = ref('')
const transcriptContentElement = ref<HTMLElement | null>(null)
const visibleCards = computed(() => props.cards)
const selectedCard = computed(
  () => visibleCards.value.find((card) => card.id === selectedCardId.value) ?? null,
)
const isReady = computed(() => props.state === 'ready')
const statusMessage = computed(() => props.stateMessage || t('ai.cards_panel.services_unavailable'))
const canRefresh = computed(() => Boolean(props.showRefresh && props.onRefresh && !isReady.value))
const internalInsightsTab = ref<'summary' | 'suggestions'>('summary')
const activeInsightsTab = computed({
  get: () => props.insightsTab ?? internalInsightsTab.value,
  set: (tab: 'summary' | 'suggestions') => {
    internalInsightsTab.value = tab
    emit('update:insightsTab', tab)
  },
})
const activeContextTab = ref<'transcript' | 'knowledge'>('transcript')
const activeContentTab = ref<'summary' | 'suggestions' | 'transcript' | 'knowledge'>(
  props.insightsTab ?? 'summary',
)

const summaryCard = computed(() => visibleCards.value.find((card) => card.id === 'ia-summary'))
const suggestionsCard = computed(() =>
  visibleCards.value.find((card) => card.id === 'ia-suggestions'),
)
const transcriptCard = computed(() =>
  visibleCards.value.find((card) => card.id === 'ia-transcript'),
)
const knowledgeCard = computed(() =>
  visibleCards.value.find((card) => card.id === 'ia-knowledge-base'),
)
const activeInsightCard = computed(() =>
  activeInsightsTab.value === 'summary' ? summaryCard.value : suggestionsCard.value,
)
const activeContextCard = computed(() =>
  activeContextTab.value === 'transcript' ? transcriptCard.value : knowledgeCard.value,
)
const activePreviewCard = computed(() =>
  activeContentTab.value === 'summary' || activeContentTab.value === 'suggestions'
    ? activeInsightCard.value
    : activeContextCard.value,
)
const transcriptItems = computed(() => transcriptCard.value?.transcriptItems ?? [])
const filteredTranscriptItems = computed(() => {
  const search = transcriptSearch.value.trim().toLowerCase()
  if (!search) return transcriptItems.value

  return transcriptItems.value.filter((item) =>
    (item.text || item.errorMessage || '').toLowerCase().includes(search),
  )
})

const copyableContent = computed(() => {
  if (activeContentTab.value === 'knowledge' || activeContentTab.value === 'transcript') {
    if (activeContentTab.value === 'knowledge') return ''

    return transcriptItems.value
      .map(
        (item) =>
          `${item.party === 'agent' ? 'Agente' : 'Cliente'}: ${item.text || item.errorMessage || ''}`,
      )
      .join('\n')
  }

  return activePreviewCard.value?.body.join('\n') ?? ''
})
const canCopyContent = computed(() => Boolean(copyableContent.value.trim()))

const insightTabs = computed(() => [
  { id: 'summary' as const, label: 'Resumen de IA', icon: FileText },
  { id: 'suggestions' as const, label: 'Sugerencias de IA', icon: Lightbulb },
])

const contextTabs = computed(() =>
  [
    transcriptCard.value || props.disableTranscript
      ? {
          id: 'transcript' as const,
          label: 'Transcripción de Llamada',
          icon: Mic,
          disabled: props.disableTranscript && !transcriptCard.value,
        }
      : null,
    { id: 'knowledge' as const, label: 'Base de Conocimiento', icon: BookOpenText },
  ].filter((tab): tab is NonNullable<typeof tab> => tab !== null),
)
const tabsGridClass = computed(() =>
  insightTabs.value.length + contextTabs.value.length === 3 ? 'grid-cols-3' : 'grid-cols-2',
)

const isAnimatedInsightCard = (card: AiCard | null | undefined) =>
  Boolean(card && (card.id.includes('summary') || card.id.includes('suggestions')))

const cardBodyKey = (card: AiCard) => `${card.id}:${card.body.join('\n')}`

const shouldShowCardItems = (card: AiCard) => Boolean(isReady.value && card.items?.length)

const transcriptTimestamp = (index: number) => {
  const seconds = index * 12
  const minutesPart = String(Math.floor(seconds / 60)).padStart(2, '0')
  const secondsPart = String(seconds % 60).padStart(2, '0')

  return `${minutesPart}:${secondsPart}`
}

const itemStatusClass = (severity?: string) => {
  if (severity === 'success') {
    return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900'
  }
  if (severity === 'info') {
    return 'bg-sky-50 text-sky-700 ring-1 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900'
  }
  if (severity === 'warn' || severity === 'warning') {
    return 'bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900'
  }
  if (severity === 'danger') {
    return 'bg-red-50 text-red-700 ring-1 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900'
  }
  if (severity === 'contrast') {
    return 'bg-slate-900 text-white ring-1 ring-slate-700 dark:bg-white dark:text-slate-900 dark:ring-slate-200'
  }
  return 'bg-slate-100 text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700'
}

const submitCardQuestion = async (card: AiCard) => {
  const question = (cardQuestionInputs.value[card.id] || '').trim()
  if (!question || !card.chat || card.chat.disabled || card.chat.loading) return

  await card.chat.onSubmit(question)
  cardQuestionInputs.value = {
    ...cardQuestionInputs.value,
    [card.id]: '',
  }
}

const setCardContentRef = (cardId: string, element: Element | ComponentPublicInstance | null) => {
  if (cardId === 'ia-transcript') {
    transcriptContentElement.value = element instanceof HTMLElement ? element : null
  }
}

const scrollTranscriptToBottom = async () => {
  await nextTick()

  const element = transcriptContentElement.value
  if (!element) return

  element.scrollTo({
    top: element.scrollHeight,
    behavior: 'smooth',
  })
}

const formatInlineSegments = (line: string): FormattedSegment[] => {
  const segments: FormattedSegment[] = []
  const pattern = /(\*\*[^*]+?\*\*|\*[^*]+?\*)/g
  let lastIndex = 0

  for (const match of line.matchAll(pattern)) {
    const index = match.index ?? 0

    if (index > lastIndex) {
      segments.push({ text: line.slice(lastIndex, index) })
    }

    const token = match[0]
    const isBold = token.startsWith('**') && token.endsWith('**')
    segments.push({
      text: isBold ? token.slice(2, -2) : token.slice(1, -1),
      bold: isBold,
      italic: !isBold,
    })

    lastIndex = index + token.length
  }

  if (lastIndex < line.length) {
    segments.push({ text: line.slice(lastIndex) })
  }

  return segments.length > 0 ? segments : [{ text: line }]
}

const refresh = async () => {
  if (!props.onRefresh || isRefreshingIaStatus.value) return

  isRefreshingIaStatus.value = true
  try {
    await props.onRefresh()
  } finally {
    isRefreshingIaStatus.value = false
  }
}

const openPreview = (card: AiCard) => {
  selectedCardId.value = card.id
  showPreviewModal.value = true
}

const selectInsightTab = (tab: 'summary' | 'suggestions') => {
  activeInsightsTab.value = tab
  activeContentTab.value = tab
}

const selectContextTab = (tab: 'transcript' | 'knowledge') => {
  activeContextTab.value = tab
  activeContentTab.value = tab
}

const copyActiveContent = async () => {
  if (!canCopyContent.value) return

  await navigator.clipboard.writeText(copyableContent.value)
  copiedContent.value = true
  toast.add({
    severity: 'success',
    summary: t('ai.cards_panel.copy_success_summary'),
    detail: t('ai.cards_panel.copy_success_detail'),
    life: 2500,
  })
  window.setTimeout(() => {
    copiedContent.value = false
  }, 1500)
}

watch(
  () => props.insightsTab,
  (tab) => {
    if (tab) activeContentTab.value = tab
  },
)

watch(
  () =>
    visibleCards.value
      .map(
        (card) =>
          `${card.id}:${card.transcriptItems?.length ?? 0}:${card.hasBlockedTranscriptItem}`,
      )
      .join('|'),
  () => {
    void scrollTranscriptToBottom()
  },
)
</script>

<template>
  <div class="relative h-full min-h-[calc(100vh-180px)] min-h-0 w-full rounded-lg pb-1">
    <div
      v-if="!isReady"
      class="app-card-glass flex min-h-[calc(100vh-130px)] h-full w-full flex-col items-center justify-center gap-4 rounded-lg px-6 py-10 text-center shadow-sm border border-surface-200/80 dark:border-surface-800 "
    >
      <div class="relative flex items-center justify-center text-[var(--p-primary-color)]">
        <div
          class="flex h-16 w-16 items-center justify-center text-[var(--p-primary-color)] dark:bg-[var(--p-primary-900)]/30"
        >
          <Sparkles :size="18" class="" />
        </div>
      </div>

      <p
        class="max-w-[18rem] text-xs font-semibold leading-5 text-surface-600 dark:text-surface-300"
      >
        {{ statusMessage }}
      </p>

      <button
        v-if="canRefresh"
        type="button"
        class="inline-flex items-center gap-2 rounded-md border border-surface-200 bg-white px-3 py-2 text-xs font-semibold text-surface-700 transition hover:border-[var(--p-primary-color)] hover:text-[var(--p-primary-color)] disabled:cursor-not-allowed disabled:opacity-60 dark:border-surface-700 dark:bg-[var(--p-surface-900)] dark:text-surface-200"
        :disabled="isRefreshingIaStatus"
        @click="refresh"
      >
        <RefreshCw class="h-3.5 w-3.5" :class="{ 'animate-spin': isRefreshingIaStatus }" />
        {{ isRefreshingIaStatus ? t('ai.cards_panel.validating') : t('ai.cards_panel.reload') }}
      </button>
    </div>

    <div v-else class="flex h-full min-h-0 flex-col gap-4">
      <section
        class="app-card-glass backdrop-blur-sm flex min-h-0 flex-1 flex-col rounded-lg shadow-sm"
      >
        <div class="flex flex-col border-b border-surface-100 px-3 pt-3 dark:border-surface-800">
          <div class="flex items-center justify-between gap-2 pb-2">
            <h2
              class="text-xs font-semibold text-[var(--p-surface-700)] dark:text-[var(--p-surface-50)]"
            >
              Asistente IA
            </h2>

            <button
              v-if="activePreviewCard"
              type="button"
              class="shrink-0 rounded-md p-1.5 text-surface-500 transition-colors hover:bg-surface-100 hover:text-[var(--p-primary-color)] dark:hover:bg-surface-800"
              @click.stop="openPreview(activePreviewCard)"
            >
              <IconExpand class="h-3.5 w-3.5" />
            </button>
          </div>

          <div :class="['grid min-w-0 flex-1 items-center gap-1', tabsGridClass]">
            <button
              v-for="tab in insightTabs"
              :key="tab.id"
              type="button"
              class="inline-flex rounded-lg w-full items-center justify-center gap-2 border-b-2 px-2.5 py-1.5 text-[11px] font-medium transition"
              :class="
                activeContentTab === tab.id
                  ? 'border-[var(--p-primary-color)] text-[var(--p-primary-color)] bg-surface-0 dark:bg-surface-800 shadow-xs'
                  : 'border-transparent text-surface-600 dark:text-surface-400 hover:text-[var(--p-primary-color)]'
              "
              @click="selectInsightTab(tab.id)"
            >
              <component :is="tab.icon" class="h-3.5 w-3.5" />
              {{ tab.label }}
            </button>
            <button
              v-for="tab in contextTabs"
              :key="tab.id"
              type="button"
              class="inline-flex rounded-lg w-full items-center justify-center gap-2 border-b-2 px-2.5 py-1.5 text-[11px] font-medium transition"
              :class="
                tab.disabled
                  ? 'cursor-not-allowed border-transparent text-surface-300 dark:text-surface-600'
                  : activeContentTab === tab.id
                    ? 'border-[var(--p-primary-color)] text-[var(--p-primary-color)] bg-surface-0 dark:bg-surface-800 shadow-xs'
                    : 'border-transparent text-surface-600 dark:text-surface-400 hover:text-[var(--p-primary-color)]'
              "
              :disabled="tab.disabled"
              @click="selectContextTab(tab.id)"
            >
              <component :is="tab.icon" class="h-3.5 w-3.5" />
              {{ tab.label }}
            </button>
          </div>
        </div>

        <div class="relative flex min-h-0 flex-1 flex-col px-4 py-3">
          <Button
            v-if="canCopyContent"
            text
            rounded
            severity="secondary"
            type="button"
            class="h-8! w-8! !absolute !right-3 !top-1 !z-10 transition-colors hover:!bg-[var(--p-primary-100)] dark:hover:!bg-[var(--p-primary-800)]/50"
            :title="copiedContent ? 'Contenido copiado' : 'Copiar contenido'"
            :aria-label="copiedContent ? 'Contenido copiado' : 'Copiar contenido'"
            @click="copyActiveContent"
          >
            <template #icon>
              <Check v-if="copiedContent" :size="14" class="text-emerald-500" />
              <Copy
                v-else
                :size="14"
                class="text-[var(--p-surface-600)] dark:text-[var(--p-surface-100)]"
              />
            </template>
          </Button>

          <Transition name="wipe" mode="out-in">
            <div
              v-if="
                (activeContentTab === 'summary' || activeContentTab === 'suggestions') &&
                activeInsightCard
              "
              :key="cardBodyKey(activeInsightCard)"
              class="ai-panel-scroll relative min-h-0 flex-1 overflow-y-auto rounded-md pb-5! text-xs leading-5 text-surface-700 dark:border-surface-800 dark:text-surface-200"
            >
              <div class="space-y-2 pt-6 pr-2">
                <p
                  v-for="(line, index) in activeInsightCard.body"
                  :key="`${activeInsightCard.id}-${index}`"
                >
                  <span
                    v-for="(segment, segmentIndex) in formatInlineSegments(line)"
                    :key="`${activeInsightCard.id}-${index}-${segmentIndex}`"
                    :class="{ 'font-semibold': segment.bold, italic: segment.italic }"
                    >{{ segment.text }}</span
                  >
                </p>
              </div>
            </div>

            <div
              v-else-if="activeContentTab === 'transcript'"
              key="transcript"
              class="flex min-h-0 flex-1 flex-col"
            >
              <div class="mb-3 flex items-center gap-2">
                <label
                  class="flex min-w-0 flex-1 items-center rounded-md bg-surface-50 py-2 text-surface-500"
                >
                  <AppInput
                    v-model="transcriptSearch"
                    type="search"
                    class="min-w-0 flex-1 bg-transparent text-[11px] text-surface-700 outline-none placeholder:text-surface-400 dark:text-surface-100"
                    placeholder="Buscar en la transcripción..."
                  >
                    <template #icon>
                      <Search class="h-3.5 w-3.5 shrink-0" />
                    </template>
                  </AppInput>
                </label>

              </div>

              <div
                class="ai-panel-scroll min-h-0 flex-1 space-y-2 overflow-y-auto rounded-md py-3 px-1 border border-surface-100 dark:border-surface-800"
                :ref="(element) => setCardContentRef('ia-transcript', element)"
              >
                <div
                  v-for="(item, index) in filteredTranscriptItems"
                  :key="item.id"
                  class="app-border-card-calss flex gap-2 rounded-md px-3 py-2 text-[11px] leading-4 shadow-sm"
                >
                  <span class="shrink-0 font-medium tabular-nums text-surface-400">
                    {{ transcriptTimestamp(index) }}
                  </span>
                  <p class="min-w-0 text-surface-700 dark:text-surface-200">
                    <span
                      class="font-semibold"
                      :class="
                        item.party === 'agent'
                          ? 'text-[var(--p-primary-color)]'
                          : 'text-emerald-600 dark:text-emerald-400'
                      "
                    >
                      {{
                        item.party === 'agent'
                          ? t('ai.cards_panel.agent')
                          : t('ai.cards_panel.client')
                      }}:
                    </span>
                    {{ item.text || item.errorMessage || t('ai.cards_panel.transcription_error') }}
                  </p>
                </div>

                <TypingIndicator v-if="transcriptCard?.hasBlockedTranscriptItem" class="mt-1" />
              </div>
            </div>

            <div v-else-if="knowledgeCard" key="knowledge" class="flex min-h-0 flex-1 flex-col">
              <div
                class="ai-panel-scroll min-h-0 flex-1 overflow-y-auto rounded-md border border-surface-100 bg-surface-50/60 p-3 dark:border-surface-800"
              >
                <TypingIndicator v-if="knowledgeCard.loading" />
                <div v-else-if="shouldShowCardItems(knowledgeCard)" class="space-y-2">
                  <button
                    v-for="item in knowledgeCard.items"
                    :key="item.id"
                    type="button"
                    class="app-card-glass backdrop-blur-sm flex w-full min-w-0 items-center justify-between gap-2 rounded-md border border-surface-200 p-2 text-left text-surface-700 transition hover:border-[var(--p-primary-color)] hover:bg-white dark:border-surface-700 dark:bg-[var(--p-surface-900)] dark:text-surface-200"
                    @click.stop="item.onClick?.()"
                  >
                    <span class="truncate text-[11px] leading-4">{{ item.label }}</span>
                    <span
                      v-if="item.status"
                      class="shrink-0 rounded-full px-2 py-0.5 text-[10px] leading-4"
                      :class="itemStatusClass(item.statusSeverity)"
                    >
                      {{ item.status }}
                    </span>
                  </button>
                </div>
                <div
                  v-else
                  class="space-y-2 text-xs leading-5 text-surface-700 dark:text-surface-200"
                >
                  <p
                    v-for="(line, index) in knowledgeCard.body"
                    :key="`${knowledgeCard.id}-${index}`"
                  >
                    {{ line }}
                  </p>
                </div>
              </div>
            </div>
          </Transition>

          <div
            v-if="
              activeInsightCard?.loading &&
              (activeContentTab === 'summary' || activeContentTab === 'suggestions')
            "
            class="mt-3 flex items-center gap-2 text-[10px] font-medium text-surface-500"
          >
            <span class="h-1.5 w-1.5 rounded-full bg-[var(--p-primary-color)]" />
            <span>Actualizando en tiempo real</span>
            <TypingIndicator v-if="isAnimatedInsightCard(activeInsightCard)" />
          </div>

          <form
            v-if="knowledgeCard?.chat && activeContentTab === 'knowledge'"
            class="mt-3 flex items-center gap-2"
            @submit.prevent="submitCardQuestion(knowledgeCard)"
          >
            <button
              v-if="knowledgeCard.chat.onOpen"
              type="button"
              class="shrink-0 rounded-md border border-surface-200 bg-white px-2.5 py-2 text-[11px] font-medium text-surface-700 transition hover:border-[var(--p-primary-color)] hover:text-[var(--p-primary-color)] dark:border-surface-700 dark:bg-[var(--p-surface-950)] dark:text-surface-100"
              @click.stop="knowledgeCard.chat.onOpen"
            >
              {{ knowledgeCard.chat.openLabel || t('ai.cards_panel.chat') }}
            </button>
            <input
              v-model="cardQuestionInputs[knowledgeCard.id]"
              type="text"
              class="min-w-0 flex-1 rounded-md border border-surface-200 bg-white px-3 py-2 text-[11px] text-surface-700 outline-none transition focus:border-[var(--p-primary-color)] dark:border-surface-700 dark:bg-[var(--p-surface-950)] dark:text-surface-100"
              :placeholder="knowledgeCard.chat.placeholder"
              :disabled="knowledgeCard.chat.disabled || knowledgeCard.chat.loading"
            />
            <button
              type="submit"
              class="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-[var(--p-primary-color)] text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="
                knowledgeCard.chat.disabled ||
                knowledgeCard.chat.loading ||
                !cardQuestionInputs[knowledgeCard.id]?.trim()
              "
              :aria-label="t('ai.cards_panel.send_question')"
            >
              <RefreshCw v-if="knowledgeCard.chat.loading" class="h-3.5 w-3.5 animate-spin" />
              <Send v-else class="h-3.5 w-3.5" />
            </button>
          </form>
        </div>
      </section>
    </div>
  </div>

  <AppDialog
    v-model="showPreviewModal"
    class="backdrop-blur-xl!"
    :style="{ width: '40rem' }"
  >
    <template #header>
      <label class="text-md font-medium">
        {{ selectedCard?.title || t('ai.cards_panel.ai_detail') }}</label
      >
    </template>
    <template #content>
      <div class="space-y-2 text-sm text-surface-700 leading-6">
        <template v-if="selectedCard?.transcriptItems?.length">
          <p v-for="item in selectedCard.transcriptItems" :key="`modal-${item.id}`">
            <span
              class="font-semibold"
              :class="
                item.party === 'agent'
                  ? 'text-[var(--p-primary-color)]'
                  : 'text-emerald-600 dark:text-emerald-400'
              "
            >
              {{ item.party === 'agent' ? t('ai.cards_panel.agent') : t('ai.cards_panel.client') }}:
            </span>
            <span>
              {{ item.text || item.errorMessage || t('ai.cards_panel.transcription_error') }}
            </span>
          </p>

          <TypingIndicator v-if="selectedCard.hasBlockedTranscriptItem" class="mt-1" />
        </template>

        <template v-else>
          <TypingIndicator
            v-if="
              (selectedCard?.loading && !isAnimatedInsightCard(selectedCard)) ||
              selectedCard?.hasBlockedTranscriptItem
            "
          />
          <Transition v-else-if="selectedCard" name="wipe" mode="out-in">
            <div :key="cardBodyKey(selectedCard)" class="space-y-2">
              <p v-for="(line, index) in selectedCard.body" :key="`line-${index}`">
                <span
                  v-for="(segment, segmentIndex) in formatInlineSegments(line)"
                  :key="`line-${index}-${segmentIndex}`"
                  :class="{ 'font-semibold': segment.bold, italic: segment.italic }"
                >
                  {{ segment.text }}
                </span>
              </p>
            </div>
          </Transition>
        </template>
      </div>
    </template>
  </AppDialog>
</template>

<style scoped>
.ai-panel-scroll {
  scrollbar-width: thin;
  scrollbar-color: var(--p-primary-color) var(--p-surface-100);
}

.ai-panel-scroll::-webkit-scrollbar {
  width: 5px;
}

.ai-panel-scroll::-webkit-scrollbar-track {
  background: var(--p-surface-100);
  border-radius: 999px;
}

.ai-panel-scroll::-webkit-scrollbar-thumb {
  background: color-mix(in srgb, var(--p-primary-color) 55%, transparent);
  border-radius: 999px;
}

.ai-panel-scroll::-webkit-scrollbar-thumb:hover {
  background: var(--p-primary-color);
}

.dark .ai-panel-scroll {
  scrollbar-color: var(--p-primary-color) var(--p-surface-800);
}

.dark .ai-panel-scroll::-webkit-scrollbar-track {
  background: var(--p-surface-800);
}

.dark .ai-panel-scroll::-webkit-scrollbar-thumb {
  background: color-mix(in srgb, var(--p-primary-color) 65%, transparent);
}

.wipe-leave-active {
  transition: opacity 0.25s ease;
}

.wipe-leave-to {
  opacity: 0;
}

.wipe-enter-active {
  animation: wipeReveal 0.7s ease forwards;
}

.wipe-enter-from {
  opacity: 0;
}

@keyframes wipeReveal {
  from {
    opacity: 0;
    clip-path: inset(0 0 100% 0);
  }

  to {
    opacity: 1;
    clip-path: inset(0 0 0 0);
  }
}
</style>
